import { ArrowBack, Visibility } from '@mui/icons-material';
import DataGrid, { DataGridConfig } from 'components/DataGrid';
import IconButton from 'components/IconButton';
import ModalDialog from 'components/ModalDialog';
import { ModuleGuard } from 'components/ModuleGuard';
import { useAtomValue } from 'jotai';
import { ModuleKey } from 'lib/api/company/types';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  UncontrolledTooltip,
} from 'reactstrap';

import {
  formatKPIValue,
  getKPIErrorMessage,
  getMetricOutcome,
  KPIOutcome,
} from './helper';
import KPIDashboardUnavailable from './KPIDashboardUnavailable';
import KPISectionCharts from './KPISectionCharts';
import KPISectionIcon from './KPISectionIcon';
import { kpiDashboardDataAtom } from './state';
import styles from './styles.module.css';
import { KPIHistoryData, KPIHistoryPoint, KPIMetric } from './types';

const outcomeDetails: Record<KPIOutcome, { label: string; className: string }> =
  {
    GOOD: { label: 'Increasing', className: styles.detailChangeGood },
    BAD: { label: 'Decreasing', className: styles.detailChangeBad },
    NEUTRAL: { label: 'Unchanged', className: styles.detailChangeNeutral },
  };

const KPI_HISTORY_DETAIL_LIMIT = 52;
const KPI_HISTORY_PAGE_SIZE = 10;

const DEFAULT_KPI_DETAILS_COLUMN_KEYS = [
  'label',
  'previous',
  'current',
  'change',
  'reportingDate',
  'actions',
];

const formatDetailValue = (
  value: number | null,
  unit: 'count' | 'days' | 'percent',
) => {
  if (unit !== 'days') return formatKPIValue(value, unit);
  if (value === null) return 'Not available';

  // Day-based KPI labels already identify the unit, so table values stay numeric.
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  }).format(value);
};

const formatChange = (
  current: number | null,
  previous: number | null,
  unit: 'count' | 'days' | 'percent',
) => {
  if (current === null || previous === null) return 'Not available';
  const difference = current - previous;
  const formattedDifference = formatDetailValue(
    unit === 'percent' ? Math.abs(difference) : difference,
    unit,
  );
  const signedDifference =
    unit !== 'percent' && difference > 0
      ? `+${formattedDifference}`
      : formattedDifference;
  if (previous === 0) return signedDifference;
  // Trend and Status already communicate direction, so percentage values show
  // magnitude only and do not repeat a plus or minus sign.
  const percentage = Math.abs((difference / Math.abs(previous)) * 100).toFixed(
    1,
  );
  return `${signedDifference} (${percentage}%)`;
};

interface KPIDataGridRow {
  current: string;
  change: string;
  displayOrder: number;
  key: string;
  label: string;
  metric: KPIMetric;
  previous: string;
  reportingDate: string;
  reportingDateValue: string;
  source: string;
  trend: string;
}

type KPIDataGridSortKey = 'displayOrder' | 'label' | 'reportingDateValue';

const KPI_EXPORT_COLUMNS = [
  { key: 'label', label: 'KPI' },
  { key: 'previous', label: 'Previous' },
  { key: 'current', label: 'Current' },
  { key: 'change', label: 'Change' },
  { key: 'trend', label: 'Trend' },
  { key: 'status', label: 'Status' },
  { key: 'source', label: 'Source' },
  { key: 'reportingDate', label: 'Reporting Date' },
] as const;

const getMetricStatus = (metric: KPIMetric) =>
  outcomeDetails[getMetricOutcome(metric)].label;

interface KPISectionDataGridProps {
  companyId: number;
  metrics: KPIMetric[];
}

const KPISectionDataGrid = ({
  companyId,
  metrics,
}: KPISectionDataGridProps) => {
  // Use the shared DataGrid so KPI details follow the same table behavior and
  // visual conventions as the Policy Compliance module.
  const data = useMemo<KPIDataGridRow[]>(
    () =>
      metrics.map((metric, index) => ({
        change: formatChange(metric.value, metric.previous_value, metric.unit),
        current: formatDetailValue(metric.value, metric.unit),
        // Preserve the API order so details initially match the dashboard card.
        displayOrder: index,
        key: metric.key,
        label: metric.label,
        metric,
        previous: formatDetailValue(metric.previous_value, metric.unit),
        reportingDate: metric.period_end
          ? moment(metric.period_end).format('MMM D, YYYY')
          : 'Not available',
        reportingDateValue: metric.period_end || '',
        source: metric.source,
        trend:
          metric.trend === 'NOT_AVAILABLE' ? 'Not available' : metric.trend,
      })),
    [metrics],
  );
  const [selectedMetric, setSelectedMetric] = useState<KPIMetric | null>(null);
  const [selectedMetricHistory, setSelectedMetricHistory] = useState<
    KPIHistoryPoint[]
  >([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(KPI_HISTORY_PAGE_SIZE);
  const historyRequestId = useRef(0);
  const [orderBy, setOrderBy] = useState<KPIDataGridSortKey>('displayOrder');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [enabledColumnKeys, setEnabledColumnKeys] = useState(
    DEFAULT_KPI_DETAILS_COLUMN_KEYS,
  );
  const loadMetricHistory = useCallback(
    async (metric: KPIMetric) => {
      const requestId = ++historyRequestId.current;
      setSelectedMetric(metric);
      setSelectedMetricHistory([]);
      setHistoryPage(0);
      setHistoryLoading(true);

      try {
        // Dashboard charts intentionally load seven points. Fetch the API's full
        // supported history only when requested so the main dashboard stays fast.
        const response = await callApi(
          'kpi/history/',
          'GET',
          {
            company_id: companyId,
            limit: KPI_HISTORY_DETAIL_LIMIT,
            metric_keys: metric.key,
          },
          null,
          null,
          'kpi/history/:kpi-metric-detail',
        );
        if (requestId !== historyRequestId.current || !response?.data) return;

        const history = response.data as KPIHistoryData;
        const points = history.periods.flatMap((period) =>
          period.metrics
            .filter((historyMetric) => historyMetric.metric_key === metric.key)
            .map((historyMetric) => ({
              period_end: period.period_end,
              value: historyMetric.value,
            })),
        );
        setSelectedMetricHistory(points);
      } catch (error) {
        if (requestId === historyRequestId.current) {
          showFailureToast(
            getKPIErrorMessage(error, 'Unable to load KPI history.'),
          );
        }
      } finally {
        if (requestId === historyRequestId.current) setHistoryLoading(false);
      }
    },
    [companyId],
  );

  const closeMetricHistory = () => {
    // Ignore a late response after the user closes the modal or opens another KPI.
    historyRequestId.current += 1;
    setSelectedMetric(null);
    setSelectedMetricHistory([]);
    setHistoryPage(0);
    setHistoryLoading(false);
  };

  const config = useMemo<DataGridConfig>(
    () => ({
      rowIdKey: 'key',
      columns: [
        {
          key: 'label',
          label: 'KPI',
          headerTooltip: 'KPI included in this dashboard area',
          rowCellStyle: { fontWeight: 500 },
          sortable: true,
          width: '240px',
        },
        { key: 'previous', label: 'Previous', width: '100px' },
        {
          key: 'current',
          label: 'Current',
          rowCellClassName: styles.detailCurrentValue,
          width: '100px',
        },
        {
          key: 'change',
          label: 'Change',
          width: '150px',
          overrideContents: (rowData) => {
            const metric = rowData.metric as KPIMetric;
            const changeOutcome = outcomeDetails[getMetricOutcome(metric)];
            const tooltipId = `kpi-change-${metric.key}`;
            return (
              <>
                <span className={changeOutcome.className} id={tooltipId}>
                  {String(rowData.change)}
                </span>
                <UncontrolledTooltip placement="top" target={tooltipId}>
                  {changeOutcome.label}
                </UncontrolledTooltip>
              </>
            );
          },
        },
        {
          key: 'reportingDate',
          label: 'Reporting Date',
          orderBy: 'reportingDateValue',
          sortable: true,
          width: '140px',
        },
        {
          key: 'actions',
          label: 'Actions',
          width: '76px',
          overrideContents: (rowData) => {
            const metric = rowData.metric as KPIMetric;
            return (
              <IconButton
                id={`view-kpi-history-${metric.key}`}
                iconElement={<Visibility fontSize="small" />}
                size="sm"
                tooltip={`View ${metric.label} details`}
                onClick={() => loadMetricHistory(metric)}
              />
            );
          },
        },
      ],
      // Reuse the shared Policy Compliance table control so users can choose
      // which KPI detail columns are visible without changing KPI data.
      columnsDisplayConfig: {
        enabledColumnKeys,
        defaultColumnKeys: DEFAULT_KPI_DETAILS_COLUMN_KEYS,
        // Keep Actions in the shared selector so it can be restored after a
        // user hides it, without requiring a dashboard reload.
        onColumnsDisplayChange: setEnabledColumnKeys,
      },
      sortingConfig: {
        orderBy,
        order: sortOrder,
        onOrderByChange: (value) => setOrderBy(value as KPIDataGridSortKey),
        onOrderChange: setSortOrder,
      },
    }),
    [enabledColumnKeys, loadMetricHistory, orderBy, sortOrder],
  );

  const sortedData = useMemo(() => {
    // The shared DataGrid owns sort controls; KPI rows are already loaded, so
    // apply that selected order locally instead of issuing an unnecessary API call.
    return [...data].sort((left, right) => {
      const leftValue = left[orderBy];
      const rightValue = right[orderBy];
      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue));
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, orderBy, sortOrder]);

  const historyData = useMemo(() => {
    if (!selectedMetric) return [];
    return selectedMetricHistory.map((point) => ({
      periodEnd: moment(point.period_end).format('MMM D, YYYY'),
      value: formatKPIValue(point.value, selectedMetric.unit),
    }));
  }, [selectedMetric, selectedMetricHistory]);
  const paginatedHistoryData = useMemo(
    () =>
      historyData.slice(
        historyPage * historyPageSize,
        (historyPage + 1) * historyPageSize,
      ),
    [historyData, historyPage, historyPageSize],
  );
  const historyConfig = useMemo<DataGridConfig>(
    () => ({
      rowIdKey: 'periodEnd',
      columns: [
        { key: 'periodEnd', label: 'Reporting Date' },
        { key: 'value', label: 'Value' },
      ],
      ...(historyData.length > 0
        ? {
            paginationConfig: {
              total_count: historyData.length,
              pageNumber: historyPage,
              pageSize: historyPageSize,
              // Pass options explicitly because fixed-size modal grids may not
              // render the shared component's fallback limit choices.
              rowsPerPageOptions: [10, 25, 50, 100],
              onPageChange: setHistoryPage,
              // Reuse the DataGrid's standard 10/25/50/100 limit selector and
              // return to the first page whenever the selected limit changes.
              onPageSizeChange: (pageSize: number) => {
                setHistoryPageSize(pageSize);
                setHistoryPage(0);
              },
            },
          }
        : {}),
    }),
    [historyData.length, historyPage, historyPageSize],
  );

  const exportKPIData = () => {
    setExportLoading(true);

    try {
      const exportData = sortedData.map((row) => ({
        label: row.label,
        previous: row.previous,
        current: row.current,
        change: row.change,
        trend: row.trend,
        status: getMetricStatus(row.metric),
        source: row.source,
        reportingDate: row.reportingDate,
      }));
      const exportColumns = KPI_EXPORT_COLUMNS;
      const escapeCSVValue = (value: string) =>
        `"${value.replace(/"/g, '""')}"`;
      const rows = exportData.map((row) =>
        exportColumns.map((column) => escapeCSVValue(String(row[column.key]))),
      );
      const csv = [
        exportColumns.map((column) => escapeCSVValue(column.label)),
        ...rows,
      ]
        .map((row) => row.join(','))
        .join('\r\n');
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(
        new Blob([csv], { type: 'text/csv' }),
      );
      link.href = objectUrl;
      link.download = 'kpi-details.csv';
      link.click();
      // Revoke the original object URL rather than the browser-normalized href
      // so every browser releases the temporary CSV resource reliably.
      URL.revokeObjectURL(objectUrl);
      showSuccessToast('KPI details exported successfully.');
    } catch (error) {
      showFailureToast(
        getKPIErrorMessage(error, 'Unable to export KPI details.'),
      );
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <div className={styles.detailDataGridActions}>
        <IconButton
          id="export-kpi-details"
          disabled={exportLoading}
          iconClass={
            exportLoading ? 'fa fa-spinner fa-spin' : 'fa fa-file-export'
          }
          size="sm"
          tooltip={
            exportLoading ? 'Preparing KPI export' : 'Export KPI details to CSV'
          }
          onClick={exportKPIData}
        />
      </div>
      <DataGrid config={config} data={sortedData} />
      {selectedMetric && (
        <ModalDialog
          title={`${selectedMetric.label} Details`}
          subTitle={<p>Recorded values for this KPI.</p>}
          childComponent={
            <DataGrid
              config={historyConfig}
              data={paginatedHistoryData}
              loading={historyLoading}
            />
          }
          onClose={closeMetricHistory}
        />
      )}
    </>
  );
};

const KPISectionDetailContent = () => {
  const { sectionKey } = useParams<{ sectionKey: string }>();
  const location = useLocation();
  // Return to the matching dashboard layout for both internal and client users.
  const dashboardPath = location.pathname.startsWith('/client')
    ? '/client/dashboard#kpi'
    : '/admin/dashboard#kpi';
  const dashboardData = useAtomValue(kpiDashboardDataAtom);
  const section = dashboardData?.sections.find(
    (candidate) => candidate.key === sectionKey,
  );
  const companyId = dashboardData?.company_id;
  const [historyByMetric, setHistoryByMetric] = useState<
    Record<string, KPIHistoryPoint[]>
  >({});
  const sectionHistoryRequestId = useRef(0);
  const metricKeys = useMemo(
    () => section?.metrics.map((metric) => metric.key).join(',') || '',
    [section],
  );

  useEffect(() => {
    if (!companyId || !metricKeys) {
      setHistoryByMetric({});
      return undefined;
    }

    const requestId = ++sectionHistoryRequestId.current;
    setHistoryByMetric({});

    const loadSectionHistory = async () => {
      try {
        // Fetch only this section's KPIs after View Details is opened. The main
        // dashboard no longer downloads history for every KPI on initial load.
        const response = await callApi(
          'kpi/history/',
          'GET',
          {
            company_id: companyId,
            limit: 7,
            metric_keys: metricKeys,
          },
          null,
          null,
          'kpi/history/:kpi-section-detail',
        );
        if (requestId !== sectionHistoryRequestId.current || !response?.data) {
          return;
        }

        const history = response.data as KPIHistoryData;
        const pointsByMetric: Record<string, KPIHistoryPoint[]> = {};
        // The API returns newest periods first; charts display them chronologically.
        [...history.periods].reverse().forEach((period) => {
          period.metrics.forEach((metric) => {
            const points = pointsByMetric[metric.metric_key] || [];
            points.push({ period_end: period.period_end, value: metric.value });
            pointsByMetric[metric.metric_key] = points;
          });
        });
        setHistoryByMetric(pointsByMetric);
      } catch (error) {
        if (requestId === sectionHistoryRequestId.current) {
          showFailureToast(
            getKPIErrorMessage(error, 'Unable to load KPI history.'),
          );
        }
      }
    };

    void loadSectionHistory();
    return () => {
      // Ignore a late response after tenant or KPI-section navigation.
      sectionHistoryRequestId.current += 1;
    };
  }, [companyId, metricKeys]);

  const sectionWithHistory = useMemo(
    () =>
      section
        ? {
            ...section,
            metrics: section.metrics.map((metric) => ({
              ...metric,
              history_points: historyByMetric[metric.key] || [],
            })),
          }
        : undefined,
    [historyByMetric, section],
  );

  if (dashboardData && !dashboardData.kpi_enabled) {
    return <KPIDashboardUnavailable />;
  }

  if (!dashboardData || !section) {
    return (
      <div className={styles.detailPage}>
        <Link className={styles.detailBackLink} to={dashboardPath}>
          <ArrowBack fontSize="small" />
          Back to KPI Dashboard
        </Link>
        <Alert color="warning" className="mt-3">
          KPI data is currently unavailable.
        </Alert>
      </div>
    );
  }

  const outcomes = section.metrics.map(getMetricOutcome);
  return (
    <div className={styles.detailPage}>
      <Card className={`border border-light ${styles.detailMainCard}`}>
        <CardHeader className={styles.detailMainHeader}>
          <Link className={styles.detailBackLink} to={dashboardPath}>
            <ArrowBack fontSize="small" />
            Back to KPI Dashboard
          </Link>
          <div className={styles.detailHeader}>
            <div className={styles.detailIdentity}>
              <KPISectionIcon sectionKey={section.key} />
              <div>
                <h2>{section.label}</h2>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody className={styles.detailMainBody}>
          <div className={styles.detailSummary}>
            <div className={styles.detailSummaryItem}>
              <span>Increasing</span>
              <strong className={styles.summaryGood}>
                {outcomes.filter((outcome) => outcome === 'GOOD').length}
              </strong>
            </div>
            <div className={styles.detailSummaryItem}>
              <span>Decreasing</span>
              <strong className={styles.summaryBad}>
                {outcomes.filter((outcome) => outcome === 'BAD').length}
              </strong>
            </div>
            <div className={styles.detailSummaryItem}>
              <span>Unchanged</span>
              <strong>
                {outcomes.filter((outcome) => outcome === 'NEUTRAL').length}
              </strong>
            </div>
          </div>

          <KPISectionCharts section={sectionWithHistory || section} />

          <section className={styles.detailSection}>
            <header className={styles.detailDataGridHeader}>
              <h4 className="mb-0">KPI Details</h4>
            </header>
            <div className={styles.detailDataGridBody}>
              <KPISectionDataGrid
                companyId={dashboardData.company_id}
                metrics={section.metrics}
              />
            </div>
          </section>

          <div className={styles.detailUpdatedText}>
            Updated {moment(dashboardData.as_of).format('lll')}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

const KPISectionDetail = () => (
  <>
    <ModuleGuard module={ModuleKey.KPI} fallback={<KPIDashboardUnavailable />}>
      {/* Apply the same company module check to direct detail-page navigation. */}
      <KPISectionDetailContent />
    </ModuleGuard>
    {/* This detail page is a standalone route and does not inherit the dashboard's toast container. */}
    <ToastContainer />
  </>
);

export default KPISectionDetail;
